import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import RoleService, { Role, Permission, RoleStats } from '../../../services/RoleService';

/**
 * Roles and Permissions Management Page
 * Admin interface for managing user roles and permissions with real API integration
 */
const RolesPermissionsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for roles and permissions
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);
  const [permissionCategories, setPermissionCategories] = useState<Record<string, Record<string, string>>>({});
  const [stats, setStats] = useState<RoleStats | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  
  // Form state for role creation/editing
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [] as string[]
  });
  
  // Permission filtering
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load roles, permissions, and stats in parallel
      const [rolesResponse, permissionsData, statsData] = await Promise.all([
        RoleService.getRoles({ per_page: 50 }),
        RoleService.getPermissions(),
        RoleService.getRoleStats()
      ]);

      setRoles(rolesResponse.roles);
      setAllPermissions(permissionsData.permissions);
      setPermissionCategories(permissionsData.categories);
      setStats(statsData);
    } catch (err: any) {
      console.error('Error loading roles data:', err);
      setError('Failed to load roles and permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      setError(null);
      
      // Frontend validation
      if (!roleForm.name.trim()) {
        setError('Role name is required');
        return;
      }
      if (!roleForm.display_name.trim()) {
        setError('Display name is required');
        return;
      }
      if (roleForm.permissions.length === 0) {
        setError('Please select at least one permission for this role');
        return;
      }

      const newRole = await RoleService.createRole({
        name: roleForm.name,
        display_name: roleForm.display_name,
        description: roleForm.description,
        permissions: roleForm.permissions
      });

      setRoles(prev => [...prev, newRole]);
      closeCreateDialog();
      setSuccess('Role created successfully!');
      
      // Reload stats
      loadData();
    } catch (err: any) {
      console.error('Error creating role:', err);
      setError(err.message || 'Failed to create role. Please try again.');
    }
  };

  const handleEditRole = async () => {
    if (!selectedRole) return;

    try {
      setError(null);
      
      // Frontend validation
      if (!roleForm.display_name.trim()) {
        setError('Display name is required');
        return;
      }
      if (roleForm.permissions.length === 0) {
        setError('Please select at least one permission for this role');
        return;
      }

      const updatedRole = await RoleService.updateRole(selectedRole.id, {
        display_name: roleForm.display_name,
        description: roleForm.description,
        permissions: roleForm.permissions
      });

      setRoles(prev => prev.map(role => 
        role.id === selectedRole.id ? updatedRole : role
      ));
      closeEditDialog();
      setSuccess('Role updated successfully!');
    } catch (err: any) {
      console.error('Error updating role:', err);
      setError(err.message || 'Failed to update role. Please try again.');
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    if (role.is_system) {
      setError('System roles cannot be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the role "${role.display_name}"?`)) {
      return;
    }

    try {
      setError(null);
      await RoleService.deleteRole(roleId);
      setRoles(prev => prev.filter(role => role.id !== roleId));
      setSuccess('Role deleted successfully!');
      
      // Reload stats
      loadData();
    } catch (err: any) {
      console.error('Error deleting role:', err);
      setError(err.message || 'Failed to delete role. Please try again.');
    }
  };

  const openEditDialog = (role: Role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      display_name: role.display_name,
      description: role.description,
      permissions: role.permissions || []
    });
    setSelectedCategory('all'); // Reset category filter
    setEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setSelectedRole(null);
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: []
    });
    setSelectedCategory('all'); // Reset category filter
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: []
    });
    setSelectedCategory('all');
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedRole(null);
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: []
    });
    setSelectedCategory('all');
  };

  const handlePermissionToggle = (permissionName: string) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionName)
        ? prev.permissions.filter(name => name !== permissionName)
        : [...prev.permissions, permissionName]
    }));
  };

  const selectAllPermissions = () => {
    setRoleForm(prev => ({
      ...prev,
      permissions: [...allPermissions]
    }));
  };

  const clearAllPermissions = () => {
    setRoleForm(prev => ({
      ...prev,
      permissions: []
    }));
  };

  const getCategoryPermissions = (categoryKey: string): string[] => {
    if (categoryKey === 'all') return allPermissions;
    return Object.keys(permissionCategories[categoryKey] || {});
  };

  const renderCategorizedPermissions = () => {
    return (
      <Box>
        {/* Category Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Filter by Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e: any) => setSelectedCategory(e.target.value as string)}
            label="Filter by Category"
          >
            <MenuItem value="all">All Permissions</MenuItem>
            {Object.keys(permissionCategories).map((categoryKey) => (
              <MenuItem key={categoryKey} value={categoryKey}>
                {categoryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Permission Categories */}
        <Box maxHeight={350} overflow="auto">
          {selectedCategory === 'all' ? (
            // Show all categories
            Object.entries(permissionCategories).map(([categoryKey, permissions]) => (
              <Box key={categoryKey} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {categoryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                  {Object.entries(permissions).map(([permission, description]) => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Checkbox
                          checked={roleForm.permissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{permission}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {description}
                          </Typography>
                        </Box>
                      }
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            ))
          ) : (
            // Show specific category
            <Box sx={{ pl: 2 }}>
              {Object.entries(permissionCategories[selectedCategory] || {}).map(([permission, description]) => (
                <FormControlLabel
                  key={permission}
                  control={
                    <Checkbox
                      checked={roleForm.permissions.includes(permission)}
                      onChange={() => handlePermissionToggle(permission)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{permission}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {description}
                      </Typography>
                    </Box>
                  }
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Roles & Permissions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Create Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Role Statistics */}
      {stats && (
        <Box display="flex" gap={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Roles
              </Typography>
              <Typography variant="h4">
                {stats.total_roles}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                System Roles
              </Typography>
              <Typography variant="h4">
                {stats.system_roles}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Custom Roles
              </Typography>
              <Typography variant="h4">
                {stats.custom_roles}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Permissions
              </Typography>
              <Typography variant="h4">
                {allPermissions.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Roles List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Roles
          </Typography>
          
          <List>
            {roles.map((role) => (
              <ListItem key={role.id} divider>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">{role.display_name}</Typography>
                      {role.is_system && (
                        <Chip label="System" size="small" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {role.description}
                      </Typography>
                      <Box display="flex" gap={1} mt={1} mb={1}>
                        <Chip 
                          label={`${role.permissions.length} permissions`} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${role.users_count} users`} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                      {/* Show some key permissions */}
                      {role.permissions.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                            Key Permissions:
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {role.permissions.slice(0, 5).map((permission) => (
                              <Chip 
                                key={permission}
                                label={permission}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            ))}
                            {role.permissions.length > 5 && (
                              <Chip 
                                label={`+${role.permissions.length - 5} more`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => openEditDialog(role)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  {!role.is_system && (
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteRole(role.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {roles.length === 0 && (
            <Box textAlign="center" py={4}>
              <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No roles found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create your first role to get started
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Role Name"
              value={roleForm.name}
              onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              placeholder="e.g., custom_manager"
            />
            <TextField
              label="Display Name"
              value={roleForm.display_name}
              onChange={(e) => setRoleForm(prev => ({ ...prev, display_name: e.target.value }))}
              fullWidth
              placeholder="e.g., Custom Manager"
            />
            <TextField
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="Describe what this role can do..."
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Permissions</Typography>
              <Box>
                <Button size="small" onClick={selectAllPermissions} sx={{ mr: 1 }}>
                  Select All
                </Button>
                <Button size="small" onClick={clearAllPermissions}>
                  Clear All
                </Button>
              </Box>
            </Box>
            
            {roleForm.permissions.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select at least one permission for this role.
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selected: {roleForm.permissions.length} of {allPermissions.length} permissions
            </Typography>
            
            {renderCategorizedPermissions()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateRole} variant="contained">Create Role</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Role: {selectedRole?.display_name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Display Name"
              value={roleForm.display_name}
              onChange={(e) => setRoleForm(prev => ({ ...prev, display_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Permissions</Typography>
              <Box>
                <Button size="small" onClick={selectAllPermissions} sx={{ mr: 1 }}>
                  Select All
                </Button>
                <Button size="small" onClick={clearAllPermissions}>
                  Clear All
                </Button>
              </Box>
            </Box>
            
            {roleForm.permissions.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select at least one permission for this role.
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selected: {roleForm.permissions.length} of {allPermissions.length} permissions
            </Typography>
            
            {renderCategorizedPermissions()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button onClick={handleEditRole} variant="contained">Update Role</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RolesPermissionsPage;